import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { ArrowUp, Loader2, Radio, Search, Sparkles } from "lucide-react";
import { HomeGuide } from "@/components/home-guide";
import { getBoard, startPayment, submitListing, trackClick } from "@/lib/board.functions";
import { useSession } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BID_STEP, CATEGORIES, MIN_BID, formatIDR } from "@/lib/board-constants";
import { ListingCard, ListingRowSkeleton, type BoardListing } from "@/components/listing-card";
import { ListingDetailSheet } from "@/components/listing-detail-sheet";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { RankingSections } from "@/components/ranking-sections";
import { ActivityFeed, type ActivityItem } from "@/components/activity-feed";

import { CountUp, Reveal, ScrollProgress, WordsReveal } from "@/components/motion-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { makeFieldTracker, track } from "@/lib/analytics";


export const Route = createFileRoute("/")({
  loader: async () => getBoard(),
  head: () => ({
    meta: [
      { title: "jasasebar.com — Papan Promosi Jasa Berperingkat" },
      {
        name: "description",
        content:
          "Sebar jasa dan produk Anda di papan peringkat live. Daftar gratis, naikkan peringkat dengan bayar seikhlasnya via pembayaran online.",
      },
      { property: "og:title", content: "jasasebar.com — Papan Promosi Jasa Berperingkat" },
      {
        property: "og:description",
        content: "Papan promosi live: daftar gratis, naik peringkat kapan saja, bayar aman lewat pembayaran online.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "jasasebar.com",
          url: "/",
          description: "Papan promosi jasa berperingkat dengan pembayaran online.",
        }),
      },
    ],
  }),
  component: BoardPage,
});

type SortKey = "peringkat" | "bid" | "klik" | "poin" | "baru";
type PeriodKey = "semua" | "7" | "30";

const SORTS: { id: SortKey; label: string }[] = [
  { id: "peringkat", label: "Peringkat" },
  { id: "bid", label: "Bid tertinggi" },
  { id: "klik", label: "Klik terbanyak" },
  { id: "poin", label: "Poin" },
  { id: "baru", label: "Terbaru" },
];

const PERIODS: { id: PeriodKey; label: string }[] = [
  { id: "semua", label: "Semua" },
  { id: "7", label: "7 hari" },
  { id: "30", label: "30 hari" },
];

/** Berlangganan SSE papan: memuat ulang data begitu ada bid/klik baru. */
function useBoardRealtime() {
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined" || !("EventSource" in window)) return;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let closedByUs = false;
    let version = "";

    const connect = () => {
      source = new EventSource("/api/public/board-stream");
      source.addEventListener("open", () => setLive(true));
      source.addEventListener("version", (event) => {
        setLive(true);
        try {
          const next = (JSON.parse((event as MessageEvent).data) as { version: string }).version;
          if (version && next !== version) {
            void queryClient.invalidateQueries({ queryKey: ["board"] });
            setPulse((p) => p + 1);
          }
          version = next;
        } catch {
          /* abaikan payload rusak */
        }
      });
      source.addEventListener("error", () => {
        setLive(false);
        source?.close();
        if (!closedByUs) retry = setTimeout(connect, 5000);
      });
    };
    connect();

    return () => {
      closedByUs = true;
      if (retry) clearTimeout(retry);
      source?.close();
    };
  }, [queryClient]);
  return { live, pulse };
}

function BoardPage() {
  const boardFn = useServerFn(getBoard);
  const payFn = useServerFn(startPayment);
  const clickFn = useServerFn(trackClick);
  const queryClient = useQueryClient();
  const { live, pulse } = useBoardRealtime();
  const [highlight, setHighlight] = useState(false);

  const initialBoard = Route.useLoaderData();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["board"],
    queryFn: () => boardFn(),
    initialData: initialBoard,
    refetchInterval: live ? 3 * 60_000 : 20000,
    refetchOnWindowFocus: true,
  });


  useEffect(() => {
    if (pulse === 0) return;
    setHighlight(true);
    const t = setTimeout(() => setHighlight(false), 1400);
    return () => clearTimeout(t);
  }, [pulse]);

  const listings = (data?.listings ?? []) as BoardListing[];
  const stats = data?.stats ?? { entries: 0, value: 0, clicks: 0 };
  const activity = (data?.activity ?? []) as ActivityItem[];
  const topAmount = listings[0]?.total_amount ?? 0;
  const claimPrice = Math.max(MIN_BID, topAmount + BID_STEP);

  const [filter, setFilter] = useState<string>("Semua");
  const [sort, setSort] = useState<SortKey>("peringkat");
  const [period, setPeriod] = useState<PeriodKey>("semua");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<BoardListing | null>(null);

  const visible = useMemo(() => {
    const cutoff = period === "semua" ? 0 : Date.now() - Number(period) * 86400000;
    const term = q.trim().toLowerCase();
    const rows = listings.filter(
      (l) =>
        (filter === "Semua" || l.category === filter) &&
        (cutoff === 0 || new Date(l.created_at).getTime() >= cutoff) &&
        (term === "" || l.name.toLowerCase().includes(term) || l.description.toLowerCase().includes(term)),
    );
    const sorted = [...rows];
    if (sort === "bid") sorted.sort((a, b) => b.total_amount - a.total_amount);
    else if (sort === "klik") sorted.sort((a, b) => b.clicks - a.clicks);
    else if (sort === "poin") sorted.sort((a, b) => b.points - a.points);
    else if (sort === "baru")
      sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return sorted;
  }, [listings, filter, sort, period, q]);

  const pay = useMutation({
    mutationFn: (input: { slug: string; amount: number }) =>
      payFn({ data: { ...input, kind: "bid" as const, origin: window.location.origin } }),
    onSuccess: (res) => {
      window.location.href = res.paymentUrl;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function openListing(slug: string, url: string) {
    void clickFn({ data: { slug } }).then(() => queryClient.invalidateQueries({ queryKey: ["board"] }));
    window.open(url, "_blank", "noopener");
  }

  function outbid(listing: BoardListing) {
    const amount = Math.max(MIN_BID, listing.total_amount + BID_STEP);
    pay.mutate({ slug: listing.slug, amount });
  }

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />

      <section id="papan" className="hero-canvas relative overflow-hidden">
        <div className="grid-fade pointer-events-none absolute inset-0" />
        <div className="relative mx-auto grid max-w-6xl gap-6 px-4 pt-8 pb-12 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:gap-8">
          {/* KIRI — konteks & aksi */}
          <motion.aside
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="lg:sticky lg:top-20 lg:self-start"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="live-dot size-1.5 rounded-full bg-success" /> Papan bergerak secara langsung
            </span>
            <h1 className="mt-4 font-display text-3xl leading-[1.08] font-semibold sm:text-4xl">
              <WordsReveal text="Papan peringkat" />{" "}
              <span className="text-gradient">
                <WordsReveal text="jasa & produk" />
              </span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Daftarkan jasa Anda gratis. Kapan pun ingin naik, bayar sesuai posisi yang diincar — aman lewat pembayaran
              online, peringkat berubah otomatis.
            </p>

            <div className="mt-5 grid grid-cols-3 gap-2">
              <StatTile label="Listing" value={stats.entries} />
              <StatTile label="Nilai papan" value={stats.value} money />
              <StatTile label="Klik" value={stats.clicks} />
            </div>

            <div className="mt-4">
              <ClaimCard claimPrice={claimPrice} listings={listings} />
            </div>

            <div id="daftar" className="mt-4">
              <SubmitForm />
            </div>
          </motion.aside>


          {/* KANAN — papan berkala di atas live */}
          <div className="min-w-0">
            <RankingSections />

            <div className="mt-6">
              <ActivityFeed items={activity} />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-semibold">
                Live sekarang{" "}
                <span className="text-sm font-normal text-muted-foreground">({visible.length} listing)</span>
              </h2>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                {isFetching && !isLoading ? (
                  <Loader2 className="size-3.5 animate-spin text-primary" />
                ) : (
                  <Radio className={`size-3.5 ${live ? "text-success" : "text-primary"}`} />
                )}{" "}
                {isFetching && !isLoading
                  ? "Memperbarui papan…"
                  : live
                    ? "realtime — otomatis saat ada bid baru"
                    : "diperbarui tiap 20 detik"}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[180px] flex-1">
                <Search className="pointer-events-none absolute inset-y-0 left-3 my-auto size-3.5 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari listing…"
                  aria-label="Cari listing"
                  className="h-9 w-full min-w-0 pl-8 text-sm"
                />
              </div>
              <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
                <SelectTrigger className="h-9 w-auto text-xs" aria-label="Urutkan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      Urut: {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex h-9 items-center gap-0.5 rounded-md border border-border bg-card p-0.5">
                {PERIODS.map((p) => (
                  <Button
                    key={p.id}
                    size="sm"
                    variant={period === p.id ? "default" : "ghost"}
                    className="h-8 px-2.5 text-xs"
                    onClick={() => setPeriod(p.id)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="no-scrollbar mt-2.5 flex gap-1.5 overflow-x-auto pb-2">
              {["Semua", ...CATEGORIES].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    filter === cat
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {isLoading && (
              <div className="mt-2 space-y-2.5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListingRowSkeleton key={i} />
                ))}
              </div>
            )}
            {error && (
              <p className="surface-card mt-3 p-5 text-sm text-destructive">
                Gagal memuat papan: {(error as Error).message}
              </p>
            )}
            {!isLoading && visible.length === 0 && (
              <p className="surface-card mt-3 p-6 text-sm text-muted-foreground">
                Tidak ada listing yang cocok dengan filter ini. Coba ubah kategori/periode, atau daftarkan gratis di
                bawah.
              </p>
            )}

            <motion.div
              animate={
                highlight
                  ? { boxShadow: "0 0 0 2px hsl(var(--primary) / 0.45)", scale: 1.004 }
                  : { boxShadow: "0 0 0 0px hsl(var(--primary) / 0)", scale: 1 }
              }
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="mt-2 space-y-2.5 rounded-2xl"
            >
              <AnimatePresence initial={false} mode="popLayout">
                {visible.map((listing, i) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    rank={i + 1}
                    onSelect={setSelected}
                    onOutbid={outbid}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      <ListingDetailSheet
        listing={selected}
        rank={selected ? visible.findIndex((l) => l.id === selected.id) + 1 : 0}
        onClose={() => setSelected(null)}
        onOpen={openListing}
        onOutbid={outbid}
      />

      <ActivityStrip activity={data?.activity ?? []} />

      <HomeGuide />


      <SiteFooter />
    </div>
  );
}



function StatTile({ label, value, money }: { label: string; value: number; money?: boolean }) {
  return (
    <Reveal className="surface-card flex flex-col justify-between p-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="mt-3 font-display text-xl font-semibold tabular-nums">
        <CountUp value={value} {...(money ? { format: (v: number) => formatIDR(Math.round(v)) } : {})} />
      </span>
    </Reveal>
  );
}

const digits = (s: string) => s.replace(/\D+/g, "");
const groupIDR = (n: number) => (n > 0 ? n.toLocaleString("id-ID") : "");

function ClaimCard({ claimPrice, listings }: { claimPrice: number; listings: BoardListing[] }) {
  const payFn = useServerFn(startPayment);
  const [slug, setSlug] = useState("");
  const [raw, setRaw] = useState(String(claimPrice));
  const amount = Number(digits(raw) || 0);

  const invalid =
    !slug
      ? "Pilih listing Anda terlebih dahulu."
      : amount < MIN_BID
        ? `Minimal ${formatIDR(MIN_BID)}.`
        : amount % BID_STEP !== 0
          ? `Gunakan kelipatan ${formatIDR(BID_STEP)}.`
          : null;

  const pay = useMutation({
    mutationFn: () => {
      track("bid_start", { slug, amount });
      return payFn({ data: { slug, amount, kind: "bid" as const, origin: window.location.origin } });
    },
    onSuccess: (res) => {
      track("bid_redirect", { slug, amount });
      toast.success("Mengarahkan ke halaman pembayaran…");
      window.location.href = res.paymentUrl;
    },
    onError: (e: Error) => {
      track("bid_error", { slug, message: e.message });
      toast.error(e.message || "Gagal membuat pembayaran. Coba lagi.");
    },
  });

  return (
    <div className="surface-card w-full min-w-0 overflow-hidden p-4 sm:p-5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
        <h2 className="truncate font-display text-base font-semibold">Rebut peringkat #1</h2>
        <span className="shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-[11px] text-accent-foreground">
          mulai {formatIDR(claimPrice)}
        </span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Pilih listing Anda, tentukan nominal. Bayar lebih kecil tetap menempatkan Anda di posisi yang sesuai.
      </p>

      <div className="mt-4 grid gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="claim-listing" className="text-xs">Listing</Label>
          <Select value={slug} onValueChange={(v) => { setSlug(v); track("form_field_filled", { form: "bid", field: "listing" }); }}>
            <SelectTrigger id="claim-listing" className="w-full min-w-0">
              <SelectValue placeholder="Pilih listing Anda…" />
            </SelectTrigger>
            <SelectContent>
              {listings.map((l) => (
                <SelectItem key={l.id} value={l.slug}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="claim-amount" className="text-xs">Nominal</Label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
              Rp
            </span>
            <Input
              id="claim-amount"
              inputMode="numeric"
              autoComplete="off"
              value={groupIDR(amount)}
              onChange={(e) => setRaw(digits(e.target.value))}
              onBlur={() => track("form_field_filled", { form: "bid", field: "amount" })}
              placeholder="0"
              className="w-full min-w-0 pl-9 tabular-nums"
              aria-invalid={Boolean(invalid && amount > 0)}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {invalid && amount > 0 ? invalid : `Minimal ${formatIDR(MIN_BID)} · kelipatan ${formatIDR(BID_STEP)}`}
          </p>
        </div>

        <Button
          className="w-full"
          disabled={Boolean(invalid) || pay.isPending}
          onClick={() => { track("cta_click", { cta: "naikkan_peringkat" }); pay.mutate(); }}
        >
          {pay.isPending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
          Naikkan peringkat
        </Button>
      </div>
    </div>
  );
}

function SubmitForm() {
  const submitFn = useServerFn(submitListing);
  const queryClient = useQueryClient();
  const session = useSession();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", description: "", category: CATEGORIES[0]! });
  const fieldTracker = useRef(makeFieldTracker("pendaftaran"));

  const mustLogin = session.enabled && !session.isAuthenticated;
  const nameOk = form.name.trim().length >= 2;
  const urlOk = form.url.trim().length >= 3;

  const submit = useMutation({
    mutationFn: () => {
      track("form_submit_attempt", { form: "pendaftaran", category: form.category });
      return (async () => {
        const idToken = await session.getIdToken();
        return submitFn({
          data: {
            ...form,
            ...(session.email ? { ownerEmail: session.email } : {}),
            ...(idToken ? { idToken } : {}),
          },
        });
      })();
    },
    onSuccess: (res) => {
      setConfirmOpen(false);
      if (res?.manage_token) localStorage.setItem(`jasasebar:${res.slug}`, res.manage_token);
      track("form_submit_success", { form: "pendaftaran", category: form.category });
      toast.success("Pendaftaran berhasil!", {
        description: "Listing Anda sudah masuk papan. Buka halaman detail untuk mengelola auto-bid.",
      });
      setForm({ name: "", url: "", description: "", category: CATEGORIES[0]! });
      fieldTracker.current = makeFieldTracker("pendaftaran");
      queryClient.invalidateQueries({ queryKey: ["board"] });
    },
    onError: (e: Error) => {
      track("form_submit_error", { form: "pendaftaran", message: e.message });
      toast.error("Pendaftaran gagal", { description: e.message || "Silakan periksa data dan coba lagi." });
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="surface-card w-full min-w-0 overflow-hidden p-4 sm:p-5"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Sparkles className="size-4 shrink-0 text-primary" />
        <h2 className="truncate font-display text-base font-semibold">Daftarkan jasa Anda — gratis</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Website, akun sosial media, atau katalog online. Listing gratis langsung tampil di papan peringkat.
        {mustLogin ? " Masuk dengan Google dulu untuk menyebar listing Anda." : ""}
      </p>

      <div className={mustLogin ? "pointer-events-none mt-4 grid gap-3 opacity-50" : "mt-4 grid gap-3"} aria-disabled={mustLogin}>
        <div className="grid gap-1.5">
          <Label htmlFor="daftar-nama" className="text-xs">Nama jasa / brand</Label>
          <Input
            id="daftar-nama"
            placeholder="Contoh: Studio Kopi Senja"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onBlur={(e) => fieldTracker.current("name", e.target.value)}
            className="w-full min-w-0"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="daftar-url" className="text-xs">Tautan</Label>
          <Input
            id="daftar-url"
            placeholder="jasasebar.com atau @akun"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            onBlur={(e) => fieldTracker.current("url", e.target.value)}
            className="w-full min-w-0"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="daftar-kategori" className="text-xs">Kategori</Label>
          <Select
            value={form.category}
            onValueChange={(v) => { setForm({ ...form, category: v }); fieldTracker.current("category", v); }}
          >
            <SelectTrigger id="daftar-kategori" className="w-full min-w-0">
              <SelectValue placeholder="Pilih kategori" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="daftar-deskripsi" className="text-xs">Deskripsi singkat</Label>
          <Input
            id="daftar-deskripsi"
            placeholder="Apa yang Anda tawarkan?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            onBlur={(e) => fieldTracker.current("description", e.target.value)}
            className="w-full min-w-0"
          />
        </div>
      </div>

      {mustLogin && (
        <Button className="mt-3 w-full" onClick={session.login} disabled={session.isLoading}>
          {session.isLoading ? <Loader2 className="size-4 animate-spin" /> : null}
          Masuk dengan Google untuk sebar
        </Button>
      )}

      <Button
        variant="secondary"
        className={mustLogin ? "hidden" : "mt-3 w-full"}
        disabled={mustLogin || !nameOk || !urlOk || submit.isPending}
        onClick={() => { track("cta_click", { cta: "tambahkan_gratis" }); setConfirmOpen(true); }}
      >
        {submit.isPending && <Loader2 className="size-4 animate-spin" />}
        Tambahkan gratis
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !submit.isPending && setConfirmOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi pendaftaran</AlertDialogTitle>
            <AlertDialogDescription>Periksa detail berikut sebelum listing dikirim ke papan.</AlertDialogDescription>
          </AlertDialogHeader>
          <dl className="grid gap-2 rounded-xl border border-border bg-card p-3 text-sm">
            <ConfirmRow label="Nama" value={form.name} />
            <ConfirmRow label="Tautan" value={form.url} />
            <ConfirmRow label="Kategori" value={form.category} />
            <ConfirmRow label="Deskripsi" value={form.description || "—"} />
            <ConfirmRow label="Posisi awal" value="Gratis, tanpa bid" />
            {session.email && <ConfirmRow label="Akun" value={session.email} />}
          </dl>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submit.isPending}>Periksa lagi</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                submit.mutate();
              }}
              disabled={submit.isPending}
            >
              {submit.isPending && <Loader2 className="size-4 animate-spin" />}
              Ya, kirim sekarang
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

function ConfirmRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[92px_minmax(0,1fr)] items-start gap-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-sm">{value}</dd>
    </div>
  );
}

function ActivityStrip({
  activity,
}: {
  activity: { name: string; slug: string; amount: number; total_after: number; created_at: string }[];
}) {
  if (activity.length === 0) return null;
  const items = [...activity, ...activity];
  return (
    <section className="mt-16 overflow-hidden border-y border-border/70 bg-card/60 py-4">
      <div className="marquee-track flex w-max gap-8 px-4">
        {items.map((a, i) => (
          <span key={i} className="flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" />
            <strong className="font-medium text-foreground">{a.name}</strong>
            naik ke {formatIDR(a.total_after)}
          </span>
        ))}
      </div>
    </section>
  );
}
